// src/components/modals/EditRawMaterialModal.jsx
import { useState, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import RawMaterialsService from '../../services/rawMaterialsService'
import { RawMaterialModel } from '../../utils/rawMaterialModel'

const EditRawMaterialModal = ({ isOpen, onClose, rawMaterial, onMaterialUpdated, units = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    unit: '',
    threshold: '',
    unitPrice: '',
    description: '',
    isActive: true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Initialize form with raw material data
  useEffect(() => {
    if (isOpen && rawMaterial) {
      console.log('🔄 Initializing edit form with material:', rawMaterial)
      
      // Handle unit field properly - extract ID if it's a populated object
      let unitValue = ''
      if (rawMaterial.unit) {
        if (typeof rawMaterial.unit === 'object' && rawMaterial.unit._id) {
          // Unit is populated object, use the _id
          unitValue = rawMaterial.unit._id
        } else if (typeof rawMaterial.unit === 'string') {
          // Unit is already a string ID
          unitValue = rawMaterial.unit
        }
      }
      
      setFormData({
        name: rawMaterial.name || '',
        code: rawMaterial.code || '',
        unit: unitValue,
        threshold: rawMaterial.threshold?.toString() || '',
        unitPrice: rawMaterial.unitPrice?.toString() || '',
        description: rawMaterial.description || '',
        isActive: rawMaterial.isActive !== undefined ? rawMaterial.isActive : true
      })
      setErrors({})
    }
  }, [isOpen, rawMaterial])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle status toggle
  const handleStatusToggle = () => {
    setFormData(prev => {
      const newStatus = !prev.isActive
      console.log('🔄 Status toggle in form:', { from: prev.isActive, to: newStatus })
      return {
        ...prev,
        isActive: newStatus
      }
    })
  }

  // Validate form using the model
  const validateForm = () => {
    const validation = RawMaterialModel.validate(formData)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)
      
      // Prepare data for API - only send changed fields
      const updateData = {}
      
      console.log('🔍 Comparing form data with original:', {
        original: rawMaterial,
        form: formData
      })
      
      if (formData.name.trim() !== rawMaterial.name) {
        updateData.name = formData.name.trim()
      }
      
      if (formData.code.trim().toUpperCase() !== rawMaterial.code) {
        updateData.code = formData.code.trim().toUpperCase()
      }
      
      // Unit field is excluded from updates as per API restrictions
      // Unit cannot be changed after creation
      
      if (Number(formData.threshold) !== rawMaterial.threshold) {
        updateData.threshold = Number(formData.threshold)
      }
      
      // Always include unitPrice in update payload
      updateData.unitPrice = Number(formData.unitPrice)
      
      if (formData.description.trim() !== (rawMaterial.description || '')) {
        updateData.description = formData.description.trim()
      }
      
      if (formData.isActive !== rawMaterial.isActive) {
        updateData.isActive = formData.isActive
        console.log('📝 Status change detected:', {
          from: rawMaterial.isActive,
          to: formData.isActive
        })
      }
      
      // Only proceed if there are changes
      if (Object.keys(updateData).length === 0) {
        console.log('ℹ️ No changes detected')
        onClose()
        return
      }
      
      console.log('📤 Updating raw material:', {
        materialId: rawMaterial._id,
        updateData
      })
      
      const response = await RawMaterialsService.updateRawMaterial(rawMaterial._id, updateData)
      
      console.log('📡 Update response:', response)
      
      if (response && response.success) {
        console.log('✅ Raw material updated successfully')
        
        // Reset form
        setFormData({
          name: '',
          code: '',
          unit: '',
          threshold: '',
          unitPrice: '',
          description: '',
          isActive: true
        })
        setErrors({})
        
        // Notify parent component with updated data
        const updatedMaterial = response.data?.rawMaterial || { ...rawMaterial, ...updateData }
        console.log('📤 Sending updated material to parent:', updatedMaterial)
        onMaterialUpdated(updatedMaterial)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Update failed with response:', response)
        // Handle API error
        if (response && response.errors) {
          setErrors(response.errors)
        } else {
          setErrors({ 
            general: response?.message || 'Failed to update raw material. Please try again.' 
          })
        }
      }
    } catch (error) {
      console.error('❌ Error updating raw material:', error)
      
      // Enhanced error handling
      let errorMessage = 'Failed to update raw material. Please try again.'
      
      if (error.response?.status === 404) {
        errorMessage = 'Raw material not found. It may have been deleted by another user.'
      } else if (error.response?.status === 409) {
        errorMessage = 'A raw material with this name or code already exists.'
      } else if (error.response?.status === 422) {
        errorMessage = 'Please check your input and try again.'
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to update this raw material.'
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setErrors({
        general: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  // Don't render if not open or no raw material
  if (!isOpen || !rawMaterial) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 ">
          <h2 className="text-xl font-semibold text-gray-900">Edit Raw Material</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          {/* Material Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Material name *
            </label>
            <Input
              name="name"
              type="text"
              placeholder="e.g. Espresso Beans"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
              required
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Material Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Material Code *
            </label>
            <Input
              name="code"
              type="text"
              placeholder="e.g. EB1200"
              value={formData.code}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()
                handleInputChange(e)
              }}
              className={errors.code ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
              required
            />
            {errors.code && (
              <p className="text-red-600 text-sm mt-1">{errors.code}</p>
            )}
          </div>

          {/* Unit - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit *
            </label>
            <div className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
              {(() => {
                // Handle unit display - check if unit is already populated object or ID
                if (rawMaterial.unit && typeof rawMaterial.unit === 'object' && rawMaterial.unit._id) {
                  // Unit is already a populated object
                  return `${rawMaterial.unit.name} (${rawMaterial.unit.abbreviation})`
                }
                
                // Unit is an ID, find it in units array
                const unit = units.find(u => u._id === rawMaterial.unit)
                if (unit) {
                  return `${unit.name} (${unit.abbreviation})`
                }
                
                // Safe handling of unit ID display
                if (rawMaterial.unit && typeof rawMaterial.unit === 'string') {
                  return `Unit ID: ${rawMaterial.unit.substring(0, 8)}...`
                }
                
                return 'Unknown Unit'
              })()}
            </div>
            <p className="text-xs text-gray-500 mt-1">Unit cannot be changed after creation</p>
          </div>

          {/* Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Threshold *
            </label>
            <Input
              name="threshold"
              type="number"
              placeholder="e.g. 1000"
              value={formData.threshold}
              onChange={handleInputChange}
              className={errors.threshold ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
              min="0"
              step="1"
              required
            />
            {errors.threshold && (
              <p className="text-red-600 text-sm mt-1">{errors.threshold}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Low stock alert threshold quantity
            </p>
          </div>

          {/* Unit Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit Price (Rs.) *
            </label>
            <Input
              name="unitPrice"
              type="number"
              placeholder="e.g. 250"
              value={formData.unitPrice}
              onChange={handleInputChange}
              className={errors.unitPrice ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
              min="0"
              step="0.01"
              required
            />
            {errors.unitPrice && (
              <p className="text-red-600 text-sm mt-1">{errors.unitPrice}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Price per unit for this raw material
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Optional description..."
              disabled={loading}
              className={`w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            />
            {errors.description && (
              <p className="text-red-600 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <div className="flex items-center">
              <button
                type="button"
                onClick={handleStatusToggle}
                disabled={loading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500/20 ${
                  formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
             
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditRawMaterialModal